import React, { Suspense, useEffect, useLayoutEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useTheme as useNextTheme } from "next-themes";
import {
  FiMenu,
  FiHome,
  FiGrid,
  FiBarChart2,
  FiUsers,
  FiLayers,
  FiSettings,
  FiBell,
  FiSearch,
  FiSave,
  FiDownload,
  FiUser,
  FiMail,
  FiLock,
  FiLogIn,
  FiLogOut,
  FiPlus,
  FiFolder,
  FiX,
  FiDatabase,
  FiMoreVertical,
  FiTriangle,
  FiAlertTriangle,
  FiTarget,
  FiColumns,
  FiFileText,
  FiChevronDown,
  FiChevronRight,
  FiShield,
  FiLifeBuoy,
  FiSun,
  FiMoon,
  FiDollarSign,
  FiLink,
  FiBriefcase,
  FiCheckCircle,
} from "react-icons/fi";
// ─── Lazy-loaded views ────────────────────────────────────────────────────────
// Each view is bundled into its own chunk and only fetched the first time the
// corresponding tab is activated. This keeps the initial JS bundle minimal.
const Dashboard      = React.lazy(() => import("./components/views/Dashboard").then(m => ({ default: m.Dashboard })));
const AdminConsole        = React.lazy(() => import("./components/views/AdminConsole").then(m => ({ default: m.AdminConsole })));
const OrganizationPanel   = React.lazy(() => import("./components/views/OrganizationPanel").then(m => ({ default: m.OrganizationPanel })));
const LandingPage    = React.lazy(() => import("./components/views/LandingPage").then(m => ({ default: m.LandingPage })));
const ComingSoonPage = React.lazy(() => import("./components/views/ComingSoonPage"));
// ─────────────────────────────────────────────────────────────────────────────
import { AlertsPanel } from "./components/AlertsPanel";
import archiPlanLogoHorizontal from "../imageApp/ArchiPlan_4_version_horizontale.png";
import archiPlanIcon from "../imageApp/ArchiPlan_3_icone_seule.png";
// Dialogs légers — chargés immédiatement (SaveDialog, LoadDialog sont fréquemment utilisés)
import { SaveDialog } from "./components/dialogs/SaveDialog";
import { LoadDialog } from "./components/dialogs/LoadDialog";
// Dialogs lourds — lazy-loadés car ils importent jsPDF / html2canvas (vendor-pdf ~237 KB gzip)
// et ne s'ouvrent que sur une action utilisateur explicite.
const ExportDialog    = React.lazy(() => import("./components/dialogs/ExportDialog").then(m => ({ default: m.ExportDialog })));
const WorkspaceDialog = React.lazy(() => import("./components/dialogs/WorkspaceDialog").then(m => ({ default: m.WorkspaceDialog })));
// Autres dialogs — légers, lazy pour réduire le bundle principal
const NewProjectDialog  = React.lazy(() => import("./components/dialogs/NewProjectDialog").then(m => ({ default: m.NewProjectDialog })));
const JoinProjectDialog = React.lazy(() => import("./components/dialogs/JoinProjectDialog").then(m => ({ default: m.JoinProjectDialog })));
import { useProjectStore, useAuthStore } from "./store";
import { useBackendPersistence } from "./hooks/useBackendPersistence";
import { useUndoRedo } from "./hooks/useUndoRedo";
import { useNotificationSocket } from "./hooks/useNotificationSocket";
// loadSampleData est importé dynamiquement au moment du clic (voir onClick plus bas)
// afin d'éviter d'inclure ~1 100 lignes de données de démo dans le bundle initial.
import { generateFrenchPublicHolidays } from "./utils/holidays";
import { ExportProgressOverlay } from "./components/common/ExportProgressOverlay";
import { addWorkingDays } from "./utils/calendar";
import type { ProjectInvite, SearchResult } from "./types";
import { ForcePasswordChange } from "./components/common/ForcePasswordChange";
import { fetchMyProjectInvites, acceptProjectInvite } from "./api/projects";
import { RootLayout } from "./components/layout/RootLayout";
import { LanguageSwitcher } from "./components/layout/LanguageSwitcher";
import { AnimatedPage } from "./components/figma-common/AnimatedPage";
// ─── Lazy-loaded page wrappers ────────────────────────────────────────────────
// Vite creates one chunk per dynamic import. Each tab's code is only fetched
// when the user navigates to it for the first time (cached on subsequent visits).
const GanttPageWrapper          = React.lazy(() => import("./components/pages/GanttPageWrapper").then(m => ({ default: m.GanttPageWrapper })));
const KanbanPageWrapper         = React.lazy(() => import("./components/pages/KanbanPageWrapper").then(m => ({ default: m.KanbanPageWrapper })));
const SwotPageWrapper           = React.lazy(() => import("./components/pages/SwotPageWrapper").then(m => ({ default: m.SwotPageWrapper })));
const RisksPageWrapper          = React.lazy(() => import("./components/pages/RisksPageWrapper").then(m => ({ default: m.RisksPageWrapper })));
const WbsPageWrapper            = React.lazy(() => import("./components/pages/WbsPageWrapper").then(m => ({ default: m.WbsPageWrapper })));
const RaciPageWrapper           = React.lazy(() => import("./components/pages/RaciPageWrapper").then(m => ({ default: m.RaciPageWrapper })));
const TrianglePageWrapper       = React.lazy(() => import("./components/pages/TrianglePageWrapper").then(m => ({ default: m.TrianglePageWrapper })));
const BudgetPageWrapper         = React.lazy(() => import("./components/pages/BudgetPageWrapper").then(m => ({ default: m.BudgetPageWrapper })));
const ClosurePageWrapper        = React.lazy(() => import("./components/pages/ClosurePageWrapper").then(m => ({ default: m.ClosurePageWrapper })));
const ClientsPageWrapper        = React.lazy(() => import("./components/pages/ClientsPageWrapper").then(m => ({ default: m.ClientsPageWrapper })));
const SpecificationsPageWrapper = React.lazy(() => import("./components/pages/SpecificationsPageWrapper").then(m => ({ default: m.SpecificationsPageWrapper })));
const PertPageWrapper           = React.lazy(() => import("./components/pages/PertPageWrapper").then(m => ({ default: m.PertPageWrapper })));
const SupportPageWrapper        = React.lazy(() => import("./components/pages/SupportPageWrapper").then(m => ({ default: m.SupportPageWrapper })));
const SettingsPageWrapper       = React.lazy(() => import("./components/pages/SettingsPageWrapper").then(m => ({ default: m.SettingsPageWrapper })));
const UserAccountSettings       = React.lazy(() => import("./components/views/UserAccountSettings").then(m => ({ default: m.UserAccountSettings })));
// ─────────────────────────────────────────────────────────────────────────────

// GLOBAL_ROLE_LABELS moved into components via t('roles.*')

/**
 * Skeleton affiché pendant le chargement d'une vue lazy-loaded.
 * Animation pulsée via Tailwind — aucune dépendance externe.
 */
const PageSkeleton: React.FC = () => (
  <div className="flex flex-col gap-4 p-6 animate-pulse" role="status" aria-label="Chargement…">
    <div className="h-8 w-48 rounded-lg bg-muted" />
    <div className="h-4 w-full rounded bg-muted" />
    <div className="h-4 w-3/4 rounded bg-muted" />
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="h-32 rounded-xl bg-muted" />
      ))}
    </div>
  </div>
);

// ─── Écran de blocage pour les utilisateurs avec demande en attente ──────────

const PendingApprovalScreen: React.FC<{
  authUser: { orgMemberRole?: string | null };
  logout: () => void;
}> = ({ logout }) => {
  const refreshUser = useAuthStore((s) => s.refreshUser);
  const [checking, setChecking] = React.useState(false);

  const handleCheck = async () => {
    setChecking(true);
    await refreshUser();
    setChecking(false);
  };

  // Auto-refresh toutes les 30 secondes — l'utilisateur voit son accès
  // s'ouvrir dès que le gestionnaire approuve, sans avoir à recharger la page.
  useEffect(() => {
    const timer = setInterval(() => { void refreshUser(); }, 30_000);
    return () => clearInterval(timer);
  }, [refreshUser]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center space-y-8">
        <div className="flex justify-center">
          <div className="w-20 h-20 rounded-2xl bg-amber-500/15 flex items-center justify-center">
            <FiBriefcase className="w-10 h-10 text-amber-400" />
          </div>
        </div>
        <div className="space-y-3">
          <h1 className="text-2xl font-bold text-foreground">
            Demande en cours de traitement
          </h1>
          <p className="text-muted-foreground leading-relaxed">
            Votre demande d'adhésion est en cours d'examen par un gestionnaire.
            Cette page se rafraîchit automatiquement — vous serez redirigé dès
            l'approbation.
          </p>
        </div>
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-5 py-4 text-sm text-amber-600 dark:text-amber-400 text-left space-y-1">
          <p className="font-semibold">En attente de validation</p>
          <p>Un responsable de l'organisation doit valider votre accès avant que vous puissiez utiliser ArchiPlan.</p>
        </div>
        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={() => void handleCheck()}
            disabled={checking}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition disabled:opacity-60"
          >
            {checking
              ? <><span className="w-4 h-4 border-2 border-primary-foreground/40 border-t-primary-foreground rounded-full animate-spin" /> Vérification…</>
              : 'Vérifier l\'état de ma demande'
            }
          </button>
          <button
            type="button"
            onClick={logout}
            className="w-full px-4 py-2.5 rounded-lg border border-border text-foreground hover:border-red-400 hover:text-red-400 transition"
          >
            Se déconnecter
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const AuthForm: React.FC<{ onBack?: () => void }> = ({ onBack }) => {
  const { t } = useTranslation('common');
  const { resolvedTheme, setTheme: setNextTheme } = useNextTheme();
  const theme = (resolvedTheme ?? "dark") as "light" | "dark";
  const onToggleTheme = () => setNextTheme(theme === "dark" ? "light" : "dark");
  const [mode, setMode] = useState<"login" | "register" | "forgot" | "reset">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");

  // ── Organisation (inscription) ────────────────────────────────────────────
  type OrgMode = "none" | "join" | "create";
  type OrgResult = { id: string; name: string; slug: string; industry: string | null; logoUrl: string | null };
  const [orgMode, setOrgMode] = useState<OrgMode>("none");
  const [orgSearch, setOrgSearch] = useState("");
  const [orgResults, setOrgResults] = useState<OrgResult[]>([]);
  const [orgSearchLoading, setOrgSearchLoading] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<OrgResult | null>(null);
  const [newOrgName, setNewOrgName] = useState("");
  const [newOrgSeats, setNewOrgSeats] = useState(5);
  const [orgInviteToken, setOrgInviteToken] = useState<string | null>(null);
  const [orgInviteBanner, setOrgInviteBanner] = useState<string | null>(null);

  // Détection token d'invitation org dans l'URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("orgInvite");
    if (token) {
      setOrgInviteToken(token);
      setOrgInviteBanner("Vous avez été invité à rejoindre une organisation. Connectez-vous ou créez un compte pour accepter l'invitation.");
      // Nettoyer l'URL sans recharger
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // Recherche organisation avec debounce
  useEffect(() => {
    if (orgMode !== "join" || orgSearch.trim().length < 2) { setOrgResults([]); return; }
    const timer = setTimeout(async () => {
      setOrgSearchLoading(true);
      try {
        const base = (import.meta.env.VITE_API_URL as string | undefined) ?? "http://localhost:4000/api";
        const res = await fetch(`${base}/organizations/search?q=${encodeURIComponent(orgSearch)}`);
        const data = await res.json() as { organizations: OrgResult[] };
        setOrgResults(data.organizations ?? []);
      } catch { setOrgResults([]); }
      finally { setOrgSearchLoading(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [orgSearch, orgMode]);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [temporaryPassword, setTemporaryPassword] = useState<string | null>(null);
  const [resetTempPassword, setResetTempPassword] = useState("");
  const [resetNewPassword, setResetNewPassword] = useState("");
  const [resetConfirmPassword, setResetConfirmPassword] = useState("");
  const [resetChannel, setResetChannel] = useState<"email" | "sms">("email");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaTimeLeft, setMfaTimeLeft] = useState<number | null>(null);
  const [mfaResendLeft, setMfaResendLeft] = useState<number | null>(null);
  const [resetResendLeft, setResetResendLeft] = useState<number | null>(null);
  const [resetResendAvailableAt, setResetResendAvailableAt] = useState<string | null>(null);
  const {
    login,
    register,
    requestPasswordReset,
    resetPasswordWithTemp,
    verifyMfa,
    resendMfa,
    clearMfa,
    mfaRequired,
    mfaChallengeId,
    mfaMessage,
    temporaryMfaCode,
    mfaExpiresAt,
    mfaAttemptsRemaining,
    mfaLockedUntil,
    mfaResendAvailableAt,
    rememberDevice,
    setRememberDevice,
    enterGuestMode,
    refreshUser,
    isSubmitting,
    error,
  } = useAuthStore((state) => ({
    login: state.login,
    register: state.register,
    requestPasswordReset: state.requestPasswordReset,
    resetPasswordWithTemp: state.resetPasswordWithTemp,
    verifyMfa: state.verifyMfa,
    resendMfa: state.resendMfa,
    clearMfa: state.clearMfa,
    mfaRequired: state.mfaRequired,
    mfaChallengeId: state.mfaChallengeId,
    mfaMessage: state.mfaMessage,
    temporaryMfaCode: state.temporaryMfaCode,
    mfaExpiresAt: state.mfaExpiresAt,
    mfaAttemptsRemaining: state.mfaAttemptsRemaining,
    mfaLockedUntil: state.mfaLockedUntil,
    mfaResendAvailableAt: state.mfaResendAvailableAt,
    rememberDevice: state.rememberDevice,
    setRememberDevice: state.setRememberDevice,
    enterGuestMode: state.enterGuestMode,
    refreshUser: state.refreshUser,
    isSubmitting: state.isSubmitting,
    error: state.error,
  }));

  // Accepte une invitation org via token (après login ou register)
  const handleAcceptOrgInvite = async (token: string) => {
    try {
      const base = (import.meta.env.VITE_API_URL as string | undefined) ?? "http://localhost:4000/api";
      const { getAuthToken } = await import("./store/index");
      const jwt = getAuthToken();
      await fetch(`${base}/organizations/accept-invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${jwt}` },
        body: JSON.stringify({ token }),
      });
    } catch { /* silencieux — la notification in-app confirmera */ }
  };

  // Post-inscription : rejoindre / créer l'org sélectionnée
  /**
   * Effectue l'action org (join ou create) APRÈS que le token a été stocké
   * mais AVANT que l'utilisateur soit mis en store (pour que les erreurs
   * restent visibles dans le formulaire d'inscription).
   * Reçoit le jwt en paramètre pour éviter la race condition avec le store.
   */
  const handlePostRegisterOrg = async (jwt: string) => {
    const base = (import.meta.env.VITE_API_URL as string | undefined) ?? "http://localhost:4000/api";
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${jwt}`,
    };

    if (orgMode === "join" && selectedOrg) {
      const res = await fetch(`${base}/organizations/${selectedOrg.id}/join`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string; message?: string };
        const msg = body.error ?? body.message ?? `Erreur HTTP ${res.status}`;
        throw new Error(`Impossible d'envoyer la demande d'adhésion : ${msg}`);
      }
    } else if (orgMode === "create" && newOrgName.trim()) {
      const res = await fetch(`${base}/organizations`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({ name: newOrgName.trim(), totalSeats: newOrgSeats }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string; message?: string };
        const msg = body.error ?? body.message ?? `Erreur HTTP ${res.status}`;
        throw new Error(`Impossible de créer l'organisation : ${msg}`);
      }
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setInfoMessage(null);
    setTemporaryPassword(null);
    if (mode === "login") {
      await login({ email, password });
      if (orgInviteToken) await handleAcceptOrgInvite(orgInviteToken);
      return;
    }
    if (mode === "register") {
      // ─────────────────────────────────────────────────────────────────────────
      // Flux d'inscription atomique en 3 étapes :
      //
      // 1. Appel direct à l'API register (sans passer par le store) pour obtenir
      //    le JWT SANS mettre l'utilisateur en store → AuthForm reste visible.
      // 2. Action org (join/create) avec le JWT frais → erreurs visibles dans
      //    le formulaire via setInfoMessage.
      // 3. refreshUser() → lit /auth/me → met l'utilisateur en store → AuthForm
      //    se démonte et l'app ou l'écran de blocage PENDING s'affiche.
      // ─────────────────────────────────────────────────────────────────────────
      const base = (import.meta.env.VITE_API_URL as string | undefined) ?? "http://localhost:4000/api";

      // Étape 1 : inscription
      let jwt = "";
      try {
        useAuthStore.setState({ isSubmitting: true, error: null });
        const res = await fetch(`${base}/auth/register`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ email, password, name }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({})) as { message?: string };
          const msg = body.message ?? `Erreur d'inscription (${res.status})`;
          useAuthStore.setState({ error: msg, isSubmitting: false });
          setInfoMessage(msg);
          return;
        }
        const { token, refreshToken } = await res.json() as { token: string; refreshToken: string };
        jwt = token;
        // Stocker les tokens dans localStorage (sans modifier le store)
        const { setStoredAuthToken, setStoredRefreshToken } = await import("./store/index");
        setStoredAuthToken(token);
        setStoredRefreshToken(refreshToken);
      } catch (err) {
        const msg = (err instanceof Error) ? err.message : "Erreur réseau lors de l'inscription";
        useAuthStore.setState({ error: msg, isSubmitting: false });
        setInfoMessage(msg);
        return;
      }

      // Étape 2 : action org (formulaire toujours visible → erreurs affichables)
      try {
        await handlePostRegisterOrg(jwt);
        if (orgMode === "join" && selectedOrg) {
          setInfoMessage(`✓ Demande d'adhésion envoyée à « ${selectedOrg.name} ». Un gestionnaire doit valider votre accès.`);
        } else if (orgMode === "create" && newOrgName.trim()) {
          setInfoMessage(`✓ Organisation « ${newOrgName.trim()} » créée avec succès.`);
        }
      } catch (orgErr) {
        // L'inscription a réussi mais l'action org a échoué — message visible
        // dans le formulaire (qui est encore affiché à cette étape).
        setInfoMessage((orgErr as Error).message);
      }

      if (orgInviteToken) await handleAcceptOrgInvite(orgInviteToken);

      // Étape 3 : charger le profil complet → met l'utilisateur en store
      // → AuthForm se démonte → écran PENDING ou application
      await refreshUser();
      useAuthStore.setState({ isSubmitting: false });
      return;
    }
    try {
      const result = await requestPasswordReset({ email, channel: resetChannel });
      if (result.temporaryPassword) {
        setResetTempPassword(result.temporaryPassword);
      }
      const nextAvailable =
        result.resendAvailableAt ?? new Date(Date.now() + 60000).toISOString();
      setResetResendAvailableAt(nextAvailable);
      // Redirect immediately to the "set new password" form
      switchMode("reset");
    } catch (err) {
      const payload = (err as { payload?: any })?.payload;
      if (payload?.resendAvailableAt) {
        setResetResendAvailableAt(payload.resendAvailableAt);
      }
      // handled via store error state
    }
  };

  const handleResetPasswordSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (resetNewPassword !== resetConfirmPassword) return;
    try {
      await resetPasswordWithTemp(email, resetTempPassword, resetNewPassword);
      // On success the store sets user → App renders the main view automatically
    } catch {
      // handled via store error state
    }
  };

  const handleMfaSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!mfaChallengeId) return;
    await verifyMfa({ challengeId: mfaChallengeId, code: mfaCode.trim() });
    setMfaCode("");
  };

  const handleResendMfa = async () => {
    if (!mfaChallengeId) return;
    await resendMfa({ challengeId: mfaChallengeId });
  };

  const handleResendReset = async () => {
    if (!email.trim()) return;
    setInfoMessage(null);
    setTemporaryPassword(null);
    try {
      const result = await requestPasswordReset({ email, channel: resetChannel });
      setInfoMessage(result.message);
      if (result.temporaryPassword) {
        setTemporaryPassword(result.temporaryPassword);
      }
      const nextAvailable =
        result.resendAvailableAt ?? new Date(Date.now() + 60000).toISOString();
      setResetResendAvailableAt(nextAvailable);
    } catch (err) {
      const payload = (err as { payload?: any })?.payload;
      if (payload?.resendAvailableAt) {
        setResetResendAvailableAt(payload.resendAvailableAt);
      }
      // handled via store error state
    }
  };

  const switchMode = (nextMode: "login" | "register" | "forgot" | "reset") => {
    setMode(nextMode);
    setInfoMessage(null);
    setTemporaryPassword(null);
    setMfaCode("");
    clearMfa();
    if (nextMode !== "register") {
      setName("");
      setOrgMode("none");
      setOrgSearch("");
      setOrgResults([]);
      setSelectedOrg(null);
      setNewOrgName("");
      setNewOrgSeats(5);
    }
    if (nextMode === "forgot") {
      setPassword("");
    }
    if (nextMode !== "forgot" && nextMode !== "reset") {
      setResetChannel("email");
    }
    if (nextMode !== "reset") {
      setResetTempPassword("");
      setResetNewPassword("");
      setResetConfirmPassword("");
    }
  };

  const isMfaStep = mfaRequired && Boolean(mfaChallengeId);

  useEffect(() => {
    if (!isMfaStep || !mfaExpiresAt) {
      setMfaTimeLeft(null);
      return;
    }
    const update = () => {
      const diff = new Date(mfaExpiresAt).getTime() - Date.now();
      setMfaTimeLeft(diff > 0 ? Math.ceil(diff / 1000) : 0);
    };
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [isMfaStep, mfaExpiresAt]);

  useEffect(() => {
    if (!isMfaStep || !mfaResendAvailableAt) {
      setMfaResendLeft(null);
      return;
    }
    const update = () => {
      const diff = new Date(mfaResendAvailableAt).getTime() - Date.now();
      setMfaResendLeft(diff > 0 ? Math.ceil(diff / 1000) : 0);
    };
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [isMfaStep, mfaResendAvailableAt]);

  useEffect(() => {
    if ((mode !== "forgot" && mode !== "reset") || !resetResendAvailableAt) {
      setResetResendLeft(null);
      return;
    }
    const update = () => {
      const diff = new Date(resetResendAvailableAt).getTime() - Date.now();
      setResetResendLeft(diff > 0 ? Math.ceil(diff / 1000) : 0);
    };
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [mode, resetResendAvailableAt]);

  const formatCountdown = (value: number | null) => {
    if (value === null) return "—";
    const minutes = Math.floor(value / 60);
    const seconds = value % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] relative">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="absolute top-6 left-6 rtl:left-auto rtl:right-6 flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground bg-card/60 border border-border rounded-lg transition"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          Retour
        </button>
      )}
      <div className="absolute top-6 right-6 rtl:right-auto rtl:left-6 flex items-center gap-2">
        <div className="bg-card/60 border border-border rounded-lg">
          <LanguageSwitcher />
        </div>
        <button
          type="button"
          onClick={onToggleTheme}
          className="flex items-center gap-2 px-3 py-2 bg-card/60 border border-border rounded-lg text-foreground hover:border-primary transition"
        >
          {theme === "dark" ? <FiSun className="w-4 h-4" /> : <FiMoon className="w-4 h-4" />}
          <span className="text-xs font-semibold">
            {theme === "dark" ? t('auth.lightMode') : t('auth.darkMode')}
          </span>
        </button>
      </div>
      <div className="w-full max-w-md bg-card/80 border border-border rounded-2xl p-6 shadow-2xl">
        <div className="flex flex-col items-center space-y-4 mb-6 text-center">
          <div className="w-24 h-24 rounded-3xl bg-card/60 border border-border flex items-center justify-center overflow-hidden">
            <img
              src={archiPlanIcon}
              alt="ArchiPlan"
              className="w-full h-full object-cover"
              style={{ transform: "scale(2.7)" }}
            />
          </div>
          <h1 className="text-2xl font-bold text-foreground">
            {isMfaStep
              ? t('auth.mfaTitle')
              : mode === "login"
              ? t('auth.login')
              : mode === "register"
                ? t('auth.register')
                : mode === "reset"
                  ? "Nouveau mot de passe"
                  : t('auth.forgotPassword')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isMfaStep
              ? t('auth.mfaDesc')
              : mode === "login"
              ? t('auth.loginDesc')
              : mode === "register"
                ? t('auth.registerDesc')
                : mode === "reset"
                  ? "Saisissez le mot de passe temporaire reçu par email et choisissez votre nouveau mot de passe."
                  : t('auth.forgotDesc')}
          </p>
        </div>

        {isMfaStep ? (
          <form className="space-y-4" onSubmit={handleMfaSubmit}>
            {mfaMessage && (
              <div className="text-sm text-primary bg-primary/10 border border-primary/30 rounded-lg px-3 py-2">
                {mfaMessage}
              </div>
            )}
            {temporaryMfaCode && (
              <div className="text-sm text-emerald-700 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2">
                {t('auth.temporaryCode')}{' '}
                <code className="px-2 py-1 bg-card rounded-lg border border-border text-foreground">
                  {temporaryMfaCode}
                </code>
              </div>
            )}
            {(mfaExpiresAt || typeof mfaAttemptsRemaining === "number" || mfaLockedUntil) && (
              <div className="text-xs text-foreground bg-card/70 border border-border rounded-lg px-3 py-2 space-y-1">
                {mfaExpiresAt && (
                  <p>{t('auth.codeExpiration')} {formatCountdown(mfaTimeLeft)}</p>
                )}
                {typeof mfaAttemptsRemaining === "number" && (
                  <p>{t('auth.attemptsRemaining')} {mfaAttemptsRemaining}</p>
                )}
                {mfaLockedUntil && (
                  <p>
                    {t('auth.lockedUntil')}{" "}
                    {new Date(mfaLockedUntil).toLocaleTimeString()}
                  </p>
                )}
              </div>
            )}
            <label className="flex items-center gap-3 text-xs text-foreground">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-border bg-card text-primary focus:ring-primary"
                checked={rememberDevice}
                onChange={(event) => setRememberDevice(event.target.checked)}
              />
              {t('auth.rememberDevice')}
            </label>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {mfaResendLeft && mfaResendLeft > 0
                  ? t('auth.resendIn', { countdown: formatCountdown(mfaResendLeft) })
                  : t('auth.resendCode')}
              </span>
              <button
                type="button"
                onClick={handleResendMfa}
                disabled={isSubmitting || (mfaResendLeft !== null && mfaResendLeft > 0)}
                className="text-primary hover:text-primary disabled:text-muted-foreground"
              >
                {t('auth.resendMfaCode')}
              </button>
            </div>
            <label className="block text-sm text-foreground font-medium">
              {t('auth.mfaCodeLabel')}
              <div className="mt-2 flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2 focus-within:border-primary">
                <FiShield className="text-muted-foreground" />
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="123456"
                  className="bg-transparent flex-1 outline-none text-foreground placeholder:text-muted-foreground tracking-[0.3em] text-center"
                  value={mfaCode}
                  onChange={(event) => setMfaCode(event.target.value)}
                  required
                />
              </div>
            </label>
            {error && (
              <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={isSubmitting || mfaCode.trim().length < 6}
              className="w-full min-h-[44px] py-3 rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 text-foreground font-semibold flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              <FiShield />
              {isSubmitting ? t('auth.verifying') : t('auth.validateCode')}
            </button>
            <button
              type="button"
              onClick={() => switchMode("login")}
              className="w-full min-h-[44px] py-3 rounded-lg border border-border text-foreground hover:border-border transition"
            >
              {t('auth.backToLogin')}
            </button>
          </form>
        ) : (
          <>
          {mode !== "reset" && <form className="space-y-4" onSubmit={handleSubmit}>
          {/* Banner invitation org */}
          {orgInviteBanner && (
            <div className="flex items-start gap-2 bg-primary/10 border border-primary/30 rounded-lg px-3 py-2 text-sm text-primary">
              <FiBriefcase className="shrink-0 mt-0.5" />
              <span>{orgInviteBanner}</span>
            </div>
          )}

          {mode === "register" && (
            <label className="block text-sm text-foreground font-medium">
              {t('auth.fullName')}
              <div className="mt-2 flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2 focus-within:border-primary">
                <FiUser className="text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Alex Martin"
                  className="bg-transparent flex-1 outline-none text-foreground placeholder:text-muted-foreground"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  required
                />
              </div>
            </label>
          )}

          <label className="block text-sm text-foreground font-medium">
            {mode === "forgot" ? t('auth.emailAccount') : t('auth.email')}
            <div className="mt-2 flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2 focus-within:border-primary">
              <FiMail className="text-muted-foreground" />
              <input
                type="text"
                placeholder={
                  mode === "forgot" ? "vous@entreprise.com" : "vous@entreprise.com ou Admin"
                }
                className="bg-transparent flex-1 outline-none text-foreground placeholder:text-muted-foreground"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
            </div>
          </label>

          {mode === "forgot" && (
            <label className="block text-sm text-foreground font-medium">
              {t('auth.sendChannel')}
              <div className="mt-2 flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2 focus-within:border-primary">
                <select
                  className="bg-transparent flex-1 outline-none text-foreground"
                  value={resetChannel}
                  onChange={(event) => setResetChannel(event.target.value as "email" | "sms")}
                >
                  <option value="email">{t('auth.channelEmail')}</option>
                  <option value="sms">{t('auth.channelSms')}</option>
                </select>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                {t('auth.smsNote')}
              </p>
            </label>
          )}

          {mode !== "forgot" && (
            <label className="block text-sm text-foreground font-medium">
              {t('auth.password')}
              <div className="mt-2 flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2 focus-within:border-primary">
                <FiLock className="text-muted-foreground" />
                <input
                  type="password"
                  placeholder="••••••••"
                  autoComplete={mode === "register" ? "new-password" : "current-password"}
                  className="bg-transparent flex-1 outline-none text-foreground placeholder:text-muted-foreground"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                  minLength={mode === "register" ? 8 : undefined}
                />
              </div>
            </label>
          )}

          {/* ── Section Entreprise (inscription uniquement) ────────── */}
          {mode === "register" && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-foreground flex items-center gap-2">
                <FiBriefcase className="text-muted-foreground" />
                Votre entreprise <span className="text-xs text-muted-foreground font-normal">(optionnel)</span>
              </p>
              {/* Sélecteur de mode */}
              <div className="grid grid-cols-3 gap-2">
                {(["none", "join", "create"] as OrgMode[]).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => { setOrgMode(m); setSelectedOrg(null); setOrgSearch(""); setNewOrgName(""); }}
                    className={`py-2 px-2 text-xs rounded-lg border transition font-medium ${
                      orgMode === m
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border text-muted-foreground hover:border-border"
                    }`}
                  >
                    {m === "none" ? "Aucune" : m === "join" ? "Rejoindre" : "Créer"}
                  </button>
                ))}
              </div>

              {/* Rejoindre une org existante */}
              {orgMode === "join" && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2 focus-within:border-primary">
                    <FiSearch className="text-muted-foreground shrink-0" />
                    <input
                      type="text"
                      placeholder="Rechercher votre entreprise…"
                      className="bg-transparent flex-1 outline-none text-foreground placeholder:text-muted-foreground text-sm"
                      value={orgSearch}
                      onChange={(e) => { setOrgSearch(e.target.value); setSelectedOrg(null); }}
                    />
                    {orgSearchLoading && <span className="text-xs text-muted-foreground animate-pulse">…</span>}
                  </div>
                  {/* Résultats */}
                  {orgResults.length > 0 && !selectedOrg && (
                    <div className="bg-card border border-border rounded-lg overflow-hidden">
                      {orgResults.map((org) => (
                        <button
                          key={org.id}
                          type="button"
                          onClick={() => { setSelectedOrg(org); setOrgSearch(org.name); setOrgResults([]); }}
                          className="w-full text-left px-3 py-2 hover:bg-muted transition text-sm flex items-center gap-2"
                        >
                          <FiBriefcase className="text-muted-foreground shrink-0" />
                          <span className="font-medium text-foreground">{org.name}</span>
                          {org.industry && <span className="text-xs text-muted-foreground">· {org.industry}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                  {selectedOrg && (
                    <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 text-sm text-emerald-400">
                      <FiCheckCircle className="shrink-0" />
                      <span><strong>{selectedOrg.name}</strong> sélectionnée — votre demande d'adhésion sera envoyée au gestionnaire.</span>
                    </div>
                  )}
                  {orgSearch.length >= 2 && orgResults.length === 0 && !orgSearchLoading && !selectedOrg && (
                    <p className="text-xs text-muted-foreground px-1">Aucune entreprise trouvée. Vous pouvez en créer une.</p>
                  )}
                </div>
              )}

              {/* Créer une nouvelle org */}
              {orgMode === "create" && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2 focus-within:border-primary">
                    <FiBriefcase className="text-muted-foreground shrink-0" />
                    <input
                      type="text"
                      placeholder="Nom de l'entreprise"
                      className="bg-transparent flex-1 outline-none text-foreground placeholder:text-muted-foreground text-sm"
                      value={newOrgName}
                      onChange={(e) => setNewOrgName(e.target.value)}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-muted-foreground whitespace-nowrap">Licences initiales :</label>
                    <input
                      type="number"
                      min={1}
                      max={500}
                      className="w-20 bg-card border border-border rounded-lg px-2 py-1 text-sm text-foreground outline-none focus:border-primary"
                      value={newOrgSeats}
                      onChange={(e) => setNewOrgSeats(Math.max(1, parseInt(e.target.value) || 1))}
                    />
                    <span className="text-xs text-muted-foreground">utilisateurs</span>
                  </div>
                  <p className="text-xs text-muted-foreground px-1">Vous serez automatiquement propriétaire de cette organisation.</p>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          {(mode === "forgot" || mode === "register") && infoMessage && (
            <div className={`text-sm rounded-lg px-3 py-2 space-y-1 border ${
              mode === "register"
                ? "text-amber-600 bg-amber-500/10 border-amber-500/30"
                : "text-emerald-700 bg-emerald-500/10 border-emerald-500/30"
            }`}>
              <p>{infoMessage}</p>
              {temporaryPassword && (
                <p>
                  {t('auth.temporaryPassword')}{" "}
                  <code className="px-2 py-1 bg-card rounded-lg border border-border text-foreground">
                    {temporaryPassword}
                  </code>
                </p>
              )}
            </div>
          )}
          {mode === "forgot" && (
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {resetResendLeft && resetResendLeft > 0
                  ? t('auth.resendIn', { countdown: formatCountdown(resetResendLeft) })
                  : t('auth.resendPassword')}
              </span>
              <button
                type="button"
                onClick={handleResendReset}
                disabled={
                  isSubmitting ||
                  !email.trim() ||
                  (resetResendLeft !== null && resetResendLeft > 0)
                }
                className="text-primary hover:text-primary disabled:text-muted-foreground"
              >
                {t('auth.resendPasswordBtn')}
              </button>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full min-h-[44px] py-3 rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 text-foreground font-semibold flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            <FiLogIn />
            {isSubmitting
              ? t('auth.submitting')
              : mode === "login"
                ? t('auth.loginBtn')
                : mode === "register"
                  ? t('auth.registerBtn')
                  : t('auth.forgotBtn')}
          </button>
          </form>}

          {/* ── Reset password form ─────────────────────────────── */}
          {mode === "reset" && !isMfaStep && (
            <form className="space-y-4 mt-4" onSubmit={handleResetPasswordSubmit}>
              <label className="block text-sm text-foreground font-medium">
                Mot de passe temporaire
                <div className="mt-2 flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2 focus-within:border-primary">
                  <FiLock className="text-muted-foreground" />
                  <input
                    type="password"
                    placeholder="••••••••"
                    autoComplete="current-password"
                    className="bg-transparent flex-1 outline-none text-foreground placeholder:text-muted-foreground"
                    value={resetTempPassword}
                    onChange={(e) => setResetTempPassword(e.target.value)}
                    required
                  />
                </div>
              </label>

              <label className="block text-sm text-foreground font-medium">
                Nouveau mot de passe
                <div className="mt-2 flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2 focus-within:border-primary">
                  <FiLock className="text-muted-foreground" />
                  <input
                    type="password"
                    placeholder="••••••••"
                    autoComplete="new-password"
                    className="bg-transparent flex-1 outline-none text-foreground placeholder:text-muted-foreground"
                    value={resetNewPassword}
                    onChange={(e) => setResetNewPassword(e.target.value)}
                    required
                    minLength={8}
                  />
                </div>
              </label>

              <label className="block text-sm text-foreground font-medium">
                Confirmer le nouveau mot de passe
                <div className={`mt-2 flex items-center gap-2 bg-card border rounded-lg px-3 py-2 focus-within:border-primary ${resetConfirmPassword && resetConfirmPassword !== resetNewPassword ? "border-red-500" : "border-border"}`}>
                  <FiLock className="text-muted-foreground" />
                  <input
                    type="password"
                    placeholder="••••••••"
                    autoComplete="new-password"
                    className="bg-transparent flex-1 outline-none text-foreground placeholder:text-muted-foreground"
                    value={resetConfirmPassword}
                    onChange={(e) => setResetConfirmPassword(e.target.value)}
                    required
                  />
                </div>
                {resetConfirmPassword && resetConfirmPassword !== resetNewPassword && (
                  <p className="mt-1 text-xs text-red-400">Les mots de passe ne correspondent pas</p>
                )}
              </label>

              {error && (
                <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
                  {error}
                </div>
              )}

              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {resetResendLeft && resetResendLeft > 0
                    ? t('auth.resendIn', { countdown: formatCountdown(resetResendLeft) })
                    : "Renvoyer le mot de passe temporaire"}
                </span>
                <button
                  type="button"
                  onClick={handleResendReset}
                  disabled={isSubmitting || !email.trim() || (resetResendLeft !== null && resetResendLeft > 0)}
                  className="text-primary hover:text-primary disabled:text-muted-foreground"
                >
                  Renvoyer
                </button>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || !resetTempPassword || !resetNewPassword || resetNewPassword !== resetConfirmPassword}
                className="w-full min-h-[44px] py-3 rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 text-foreground font-semibold flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                <FiLock />
                {isSubmitting ? t('auth.submitting') : "Enregistrer le nouveau mot de passe"}
              </button>
            </form>
          )}
          </>
        )}

        {mode === "login" && !isMfaStep && (
          <div className="flex flex-col sm:flex-row items-center justify-between text-sm text-muted-foreground mt-6 gap-3">
            <span>
              {t('auth.noAccount')}{" "}
              <button
                type="button"
                className="text-primary hover:text-primary font-semibold"
                onClick={() => switchMode("register")}
              >
                {t('auth.signUp')}
              </button>
            </span>
            <button
              type="button"
              className="text-primary hover:text-primary font-semibold"
              onClick={() => switchMode("forgot")}
            >
              {t('auth.forgotPasswordLink')}
            </button>
          </div>
        )}

        {mode === "register" && !isMfaStep && (
          <p className="text-center text-sm text-muted-foreground mt-6">
            {t('auth.alreadyMember')}{" "}
            <button
              type="button"
              className="text-primary hover:text-primary font-semibold"
              onClick={() => switchMode("login")}
            >
              {t('auth.signIn')}
            </button>
          </p>
        )}

        {(mode === "forgot" || mode === "reset") && !isMfaStep && (
          <p className="text-center text-sm text-muted-foreground mt-6">
            {t('auth.rememberPassword')}{" "}
            <button
              type="button"
              className="text-primary hover:text-primary font-semibold"
              onClick={() => switchMode("login")}
            >
              {t('auth.backToLogin')}
            </button>
          </p>
        )}

        {!isMfaStep && (
          <div className="mt-6 border-t border-border pt-4 text-center">
            <p className="text-xs text-muted-foreground mb-3">
              {t('auth.guestMode')}
            </p>
            <button
              type="button"
              onClick={() => enterGuestMode()}
              className="w-full min-h-[44px] py-3 rounded-lg border border-border text-foreground hover:border-primary hover:text-foreground transition"
            >
              {t('auth.continueGuest')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
function App() {
  const { t } = useTranslation('common');
  const { resolvedTheme, setTheme: setNextTheme } = useNextTheme();
  const theme = (resolvedTheme ?? "dark") as "light" | "dark";
  const {
    nodes,
    edges,
    sidebarCollapsed,
    setSidebarCollapsed,
    notifications,
    markNotificationAsRead,
    clearNotifications,
    removeNotification,
    getStatistics,
    calculatePert,
    loadFromLocalStorage,
    search,
    searchQuery,
    setSearchQuery,
    clearSearch,
    selectElement,
    currentProject,
    updateProject,
    addNotification,
    createProject,
    generateGanttTasks,
    setCurrentProject,
    storageMode,
    syncWithBackend,
  } = useProjectStore();

  useBackendPersistence();
  useUndoRedo();
  useNotificationSocket();

  const [showLanding, setShowLanding] = useState(true);
  const [activeTab, setActiveTab] = useState("home");
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const exportDialogOpen    = useProjectStore((s) => s.exportDialogOpen);
  const setExportDialogOpen = useProjectStore((s) => s.setExportDialogOpen);
  const [workspaceDialogOpen, setWorkspaceDialogOpen] = useState(false);
  const [newProjectDialogOpen, setNewProjectDialogOpen] = useState(false);
  const [joinProjectDialogOpen, setJoinProjectDialogOpen] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [holidayInput, setHolidayInput] = useState("");
  const [startDateDraft, setStartDateDraft] = useState("");
  const [includeWeekendsDraft, setIncludeWeekendsDraft] = useState(true);
  const [currencyDraft, setCurrencyDraft] = useState("EUR");
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [settingsSubTab, setSettingsSubTab] = useState<"project" | "account" | "org">("account");
  const [pendingInvites, setPendingInvites] = useState<ProjectInvite[]>([]);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [invitesError, setInvitesError] = useState<string | null>(null);
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(
    new Set()
  );
  const [sampleScenario, setSampleScenario] = useState<"success" | "partial" | "failed" | "day5">("partial");
  const [guestHomeMode, setGuestHomeMode] = useState<"choice" | "tools" | "project">("choice");
  const [guestToolTab, setGuestToolTab] = useState<
    "pert" | "gantt" | "wbs" | "raci" | "kanban" | "swot" | "risks" | "triangle" | null
  >(null);
  const [editingSpecificationId, setEditingSpecificationId] = useState<string | null>(null);
  const [clientFilterId, setClientFilterId] = useState<string | null>(null);
  const headerRef = React.useRef<HTMLElement | null>(null);
  const [headerHeight, setHeaderHeight] = React.useState(96);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const bodyOverflowRef = React.useRef<string | null>(null);

  // Get specifications from store for validation
  const { specifications: allSpecifications } = useProjectStore();
  const { user: authUser, isInitializing: isAuthInitializing } = useAuthStore((state) => ({
    user: state.user,
    isInitializing: state.isInitializing,
  }));
  const isGuest = authUser?.globalRole === "GUEST" && authUser?.id === "guest-local";
  const initializeAuth = useAuthStore((state) => state.initialize);
  const logout = useAuthStore((state) => state.logout);
  const profileInitial = React.useMemo(() => {
    if (!authUser) return "?";
    const source = authUser.name && authUser.name.trim().length > 0 ? authUser.name : authUser.email;
    return source.charAt(0).toUpperCase();
  }, [authUser]);

  const searchResults = React.useMemo(
    () => (searchQuery.length >= 2 ? search(searchQuery) : []),
    [searchQuery, search]
  );

  const loadPendingInvites = React.useCallback(async () => {
    if (!authUser || storageMode !== "backend") {
      setPendingInvites([]);
      return;
    }
    setInvitesLoading(true);
    setInvitesError(null);
    try {
      const invites = await fetchMyProjectInvites();
      setPendingInvites(invites);
    } catch (error) {
      setInvitesError(
        error instanceof Error ? error.message : t('home.cannotLoadInvites')
      );
    } finally {
      setInvitesLoading(false);
    }
  }, [authUser, storageMode]);

  React.useEffect(() => {
    void loadPendingInvites();
  }, [loadPendingInvites]);

  const handleAcceptInviteFromHome = async (token: string) => {
    if (storageMode !== "backend") {
      addNotification?.("error", t('home.collabRequiresBackend'));
      return;
    }
    try {
      const { project } = await acceptProjectInvite(token);
      await syncWithBackend?.();
      setCurrentProject(project);
      addNotification?.("success", t('home.joinedProject', { name: project.name }));
      setActiveTab("dashboard");
      void loadPendingInvites();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t('home.cannotJoinProject');
      addNotification?.("error", message);
    }
  };

  const holidayList = currentProject?.settings?.holidays || [];
  const upcomingHolidays = React.useMemo(() => {
    if (!holidayList || holidayList.length === 0) return [];
    return holidayList
      .map((iso) => new Date(iso))
      .filter((date) => !Number.isNaN(date.getTime()))
      .sort((a, b) => a.getTime() - b.getTime());
  }, [holidayList.join("|")]);

  const handleStartDateChange = (value: string) => {
    setStartDateDraft(value);
  };

  const handleIncludeWeekendsChange = (checked: boolean) => {
    setIncludeWeekendsDraft(checked);
  };

  const handleHolidayInputChange = (value: string) => {
    setHolidayInput(value);
  };

  const handleAutoFillHolidays = () => {
    if (!currentProject) return;
    const baseStart = (() => {
      if (startDateDraft) {
        const parsed = new Date(startDateDraft);
        if (!Number.isNaN(parsed.getTime())) {
          return parsed;
        }
      }
      return currentProject.startDate
        ? new Date(currentProject.startDate)
        : new Date();
    })();

    const includeWeekendsValue = includeWeekendsDraft;
    const estimatedEnd =
      stats.totalDuration > 0
        ? addWorkingDays(
            baseStart,
            stats.totalDuration,
            includeWeekendsValue,
            []
          )
        : baseStart;

    const startYear = baseStart.getFullYear();
    const endYear = Math.max(estimatedEnd.getFullYear(), startYear);
    const years: number[] = [];
    for (let year = startYear; year <= endYear + 1; year += 1) {
      years.push(year);
    }

    const generated = years.flatMap((year) =>
      generateFrenchPublicHolidays(year)
    );
    const unique = Array.from(new Set(generated)).sort();
    setHolidayInput(unique.join("\n"));
    generateGanttTasks();
    addNotification(
      "success",
      t('notifications.holidaysUpdated')
    );
  };

  const handleSearchResultClick = (result: SearchResult) => {
    if (result.type === "node" || result.type === "edge") {
      setActiveTab("pert");
      selectElement(result.id);
    } else if (result.type === "member") {
      setActiveTab("raci");
    } else if (result.type === "project") {
      setActiveTab("dashboard");
    }
    setSearchFocused(false);
    clearSearch();
  };

  React.useEffect(() => {
    if (typeof document === "undefined" || typeof window === "undefined") {
      return;
    }
    const body = document.body;
    if (bodyOverflowRef.current === null) {
      bodyOverflowRef.current = body.style.overflow || "";
    }
    const shouldLockScroll = !sidebarCollapsed && window.innerWidth < 1024;
    if (shouldLockScroll) {
      body.style.overflow = "hidden";
    } else {
      body.style.overflow = bodyOverflowRef.current ?? "";
    }

    return () => {
      if (bodyOverflowRef.current !== null) {
        body.style.overflow = bodyOverflowRef.current;
      }
    };
  }, [sidebarCollapsed]);

  const unreadNotifications = notifications.filter((n) => !n.read).length;
  const startDateValue = currentProject?.startDate
    ? new Date(currentProject.startDate).toISOString().split("T")[0]
    : new Date().toISOString().split("T")[0];
  const includeWeekendsSetting =
    currentProject?.settings?.includeWeekends ?? true;

  React.useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  const toggleTheme = () => {
    setNextTheme(theme === "dark" ? "light" : "dark");
  };

  React.useEffect(() => {
    if (storageMode === 'local') {
      loadFromLocalStorage();
    }
  }, [storageMode, loadFromLocalStorage]);

  React.useEffect(() => {
    if (
      storageMode !== 'backend' ||
      !authUser ||
      authUser.passwordResetRequired
    ) {
      return;
    }
    syncWithBackend().catch((error) => {
      console.error('Backend sync failed', error);
    });
  }, [storageMode, authUser, syncWithBackend]);

  React.useEffect(() => {
    if (!currentProject) return;
    setHolidayInput((currentProject.settings.holidays || []).join("\n"));
    setStartDateDraft(startDateValue);
    setIncludeWeekendsDraft(includeWeekendsSetting);
    setCurrencyDraft(currentProject.settings.currency || "EUR");
  }, [currentProject?.id, startDateValue, includeWeekendsSetting]);

  useLayoutEffect(() => {
    const updateHeaderHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.getBoundingClientRect().height);
      }
    };
    updateHeaderHeight();
    window.addEventListener("resize", updateHeaderHeight);
    return () => {
      window.removeEventListener("resize", updateHeaderHeight);
    };
  }, []);

  React.useEffect(() => {
    if (!currentProject) return;
    generateGanttTasks();
  }, [currentProject?.id, nodes, edges, generateGanttTasks]);

  const actionsMenuRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        actionsMenuRef.current &&
        !actionsMenuRef.current.contains(event.target as Node)
      ) {
        setActionsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const profileMenuRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target as Node)
      ) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const holidayDraftList = React.useMemo(
    () =>
      holidayInput
        .split(/[\n,]/)
        .map((entry) => entry.trim())
        .filter(Boolean)
        .sort(),
    [holidayInput]
  );

  const currentHolidayList = React.useMemo(
    () =>
      [...(currentProject?.settings?.holidays || [])]
        .map((h) => h.trim())
        .filter(Boolean)
        .sort(),
    [currentProject?.settings?.holidays?.join("|")]
  );

  // Get real-time statistics
  const stats = getStatistics();
  // Removed unused formatDate function
  // const formatDate = (date: Date | null | undefined) =>
  //   date
  //     ? new Date(date).toLocaleDateString("fr-FR", {
  //         day: "2-digit",
  //         month: "short",
  //         year: "numeric",
  //       })
  //     : "Non défini";

  const hasSettingsChanges = currentProject
    ? startDateDraft !== startDateValue ||
      includeWeekendsDraft !== includeWeekendsSetting ||
      currencyDraft !== (currentProject.settings.currency || "EUR") ||
      JSON.stringify(holidayDraftList) !== JSON.stringify(currentHolidayList)
    : false;

  const handleSaveSettings = async () => {
    if (!currentProject || !startDateDraft) return;
    const parsedStart = new Date(startDateDraft);
    if (Number.isNaN(parsedStart.getTime())) {
      addNotification("error", t('notifications.invalidStartDate'));
      return;
    }
    setIsSavingSettings(true);
    try {
      updateProject({
        startDate: parsedStart,
        settings: {
          ...currentProject.settings,
          includeWeekends: includeWeekendsDraft,
          holidays: holidayDraftList,
          currency: currencyDraft,
        },
      });
      generateGanttTasks();
      addNotification("success", t('notifications.settingsSaved'));
    } finally {
      setIsSavingSettings(false);
    }
  };

  const menuCategories = React.useMemo(() => {
    if (authUser?.globalRole === "ADMIN" || authUser?.globalRole === "OWNER") {
      return [
        {
          id: "administration",
          label: t('roles.ADMIN', { ns: 'common' }),
          items: [{ id: "admin-console", label: t('adminConsole', { ns: 'sidebar' }), icon: FiShield }],
        },
      ];
    }

    if (isGuest && guestToolTab) {
      const toolItems: Record<
        NonNullable<typeof guestToolTab>,
        { id: string; label: string; icon: React.ComponentType<any> }
      > = {
        pert: { id: "pert", label: t('pertEditor', { ns: 'sidebar' }), icon: FiGrid },
        gantt: { id: "gantt", label: t('ganttDiagram', { ns: 'sidebar' }), icon: FiBarChart2 },
        wbs: { id: "wbs", label: t('wbs', { ns: 'sidebar' }), icon: FiLayers },
        raci: { id: "raci", label: t('raciMatrix', { ns: 'sidebar' }), icon: FiUsers },
        kanban: { id: "kanban", label: t('kanbanBoard', { ns: 'sidebar' }), icon: FiColumns },
        swot: { id: "swot", label: t('swotAnalysis', { ns: 'sidebar' }), icon: FiTarget },
        risks: { id: "risks", label: t('riskMatrix', { ns: 'sidebar' }), icon: FiAlertTriangle },
        triangle: { id: "triangle", label: t('goldenTriangle', { ns: 'sidebar' }), icon: FiTriangle },
      };
      return [
        {
          id: "guest-tool",
          label: t('toolMode.title'),
          items: [toolItems[guestToolTab]],
        },
      ];
    }

    const categories = [
      {
        id: "overview",
        label: t('overview', { ns: 'sidebar' }),
        items: [{ id: "dashboard", label: t('dashboard', { ns: 'sidebar' }), icon: FiHome }],
      },
      {
        id: "specifications",
        label: t('specifications', { ns: 'sidebar' }),
        items: [
          { id: "clients", label: t('clients', { ns: 'sidebar' }), icon: FiUsers },
          { id: "specifications", label: t('specsTitle', { ns: 'sidebar' }), icon: FiFileText },
        ],
      },
      {
        id: "planning",
        label: t('planning', { ns: 'sidebar' }),
        items: [
          { id: "pert", label: t('pertEditor', { ns: 'sidebar' }), icon: FiGrid },
          { id: "gantt", label: t('ganttDiagram', { ns: 'sidebar' }), icon: FiBarChart2 },
          { id: "wbs", label: t('wbs', { ns: 'sidebar' }), icon: FiLayers },
        ],
      },
      {
        id: "organization",
        label: t('organization', { ns: 'sidebar' }),
        items: [
          { id: "kanban", label: t('kanbanBoard', { ns: 'sidebar' }), icon: FiColumns },
          { id: "raci", label: t('raciMatrix', { ns: 'sidebar' }), icon: FiUsers },
        ],
      },
      {
        id: "analysis",
        label: t('analysisRisks', { ns: 'sidebar' }),
        items: [
          { id: "swot", label: t('swotAnalysis', { ns: 'sidebar' }), icon: FiTarget },
          { id: "risks", label: t('riskMatrix', { ns: 'sidebar' }), icon: FiAlertTriangle },
          { id: "triangle", label: t('goldenTriangle', { ns: 'sidebar' }), icon: FiTriangle },
          { id: "budget", label: t('budgetEvm', { ns: 'sidebar' }), icon: FiDollarSign },
        ],
      },
      {
        id: "closure",
        label: t('closure', { ns: 'sidebar' }),
        items: [{ id: "closure", label: t('projectClosure', { ns: 'sidebar' }), icon: FiFileText }],
      },
      {
        id: "config",
        label: t('configuration', { ns: 'sidebar' }),
        items: [
          { id: "settings", label: t('settings', { ns: 'sidebar' }), icon: FiSettings },
          { id: "support", label: t('support', { ns: 'sidebar' }), icon: FiLifeBuoy },
        ],
      },
    ];

    return categories;
  }, [authUser?.globalRole, isGuest, guestToolTab, t]);

  const toggleCategory = (categoryId: string) => {
    setCollapsedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const hasActiveProject = Boolean(currentProject);
  const isGlobalAdmin   = authUser?.globalRole === "ADMIN" || authUser?.globalRole === "OWNER";
  const isOrgManager    = authUser?.orgMemberRole === "OWNER" || authUser?.orgMemberRole === "MANAGER";
  const adminConsoleDisplayName =
    authUser?.name?.trim().length
      ? authUser.name
      : authUser?.username ?? authUser?.email ?? t('roles.ADMIN');
  const adminConsoleRole = authUser?.globalRole ?? "ADMIN";
  const projectRoleLabel = currentProject?.currentUserRole
    ? t(`roles.${currentProject.currentUserRole}`)
    : null;
  const projectRoleBadgeClass = currentProject?.currentUserRole
    ? currentProject.currentUserRole === "OWNER"
      ? "bg-amber-500/20 text-amber-600 border-amber-500/40"
      : currentProject.currentUserRole === "ADMIN"
        ? "bg-primary/20 text-primary border-primary/40"
        : currentProject.currentUserRole === "EDITOR"
          ? "bg-emerald-500/20 text-emerald-600 border-emerald-500/40"
          : currentProject.currentUserRole === "COMMENTATOR"
            ? "bg-purple-500/20 text-purple-600 border-purple-500/40"
            : "bg-muted/40 text-foreground border-border"
    : "bg-muted/40 text-foreground border-border";

  const prevIsGlobalAdmin = React.useRef(false);
  React.useEffect(() => {
    if (isGlobalAdmin && !prevIsGlobalAdmin.current) {
      // First time becoming admin → redirect to admin console
      setActiveTab("admin-console");
    } else if (!isGlobalAdmin && activeTab === "admin-console") {
      // Lost admin rights while on admin console → redirect away
      setActiveTab(hasActiveProject ? "dashboard" : "home");
    } else if (!isOrgManager && activeTab === "org-management") {
      // Lost org manager rights → redirect away
      setActiveTab(hasActiveProject ? "dashboard" : "home");
    }
    prevIsGlobalAdmin.current = isGlobalAdmin;
  }, [isGlobalAdmin, activeTab, hasActiveProject, setActiveTab]);

  if (import.meta.env.VITE_COMING_SOON === 'true') {
    return (
      <Suspense fallback={<div className="min-h-screen bg-gray-950" />}>
        <ComingSoonPage />
      </Suspense>
    );
  }

  if (isAuthInitializing) {
    return (
      <>
        <ExportProgressOverlay />
        <div className="min-h-screen bg-background flex items-center justify-center text-foreground">
          <div className="text-center space-y-3">
            <div className="w-12 h-12 rounded-full border-2 border-primary border-t-transparent animate-spin mx-auto" />
            <p className="text-foreground">{t('loadingSecureSpace')}</p>
          </div>
        </div>
      </>
    );
  }

  if (!authUser) {
    if (showLanding) {
      return (
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950"><div className="w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" /></div>}>
          <LandingPage
            onLogin={() => setShowLanding(false)}
            onRegister={() => setShowLanding(false)}
          />
        </Suspense>
      );
    }
    return <AuthForm onBack={() => setShowLanding(true)} />;
  }

  if (authUser.passwordResetRequired) {
    return <ForcePasswordChange />;
  }

  // ── Demande d'adhésion à une organisation en attente de validation ───────────
  // L'utilisateur a une demande d'adhésion org en attente de validation.
  // orgMemberStatus === 'PENDING' est suffisant — organizationId peut être
  // null si le backend n'a pas encore mis à jour user.organizationId
  // (seul OrgMembership.status est garanti via /auth/me).
  if (authUser.orgMemberStatus === 'PENDING') {
    return <PendingApprovalScreen authUser={authUser} logout={logout} />;
  }

  const enterGuestToolMode = async (
    tab: "pert" | "gantt" | "wbs" | "raci" | "kanban" | "swot" | "risks" | "triangle",
    label: string
  ) => {
    if (!isGuest) return;
    try {
      useProjectStore.getState().setStorageMode("local");
      const project = await createProject(
        t('toolMode.guestSessionLabel', { label }),
        t('toolMode.tempProject')
      );
      if (project) {
        setGuestToolTab(tab);
        setActiveTab(tab);
      }
    } catch (error) {
      addNotification(
        "error",
        error instanceof Error ? error.message : t('toolMode.cannotStartTool')
      );
    }
  };

  const guestToolOptions = [
    { id: "pert", label: t('pertEditor', { ns: 'sidebar' }), description: t('toolMode.pertDesc'), icon: FiGrid },
    { id: "gantt", label: t('ganttDiagram', { ns: 'sidebar' }), description: t('toolMode.ganttDesc'), icon: FiBarChart2 },
    { id: "wbs", label: t('wbs', { ns: 'sidebar' }), description: t('toolMode.wbsDesc'), icon: FiLayers },
    { id: "raci", label: t('raciMatrix', { ns: 'sidebar' }), description: t('toolMode.raciDesc'), icon: FiUsers },
    { id: "kanban", label: t('kanbanBoard', { ns: 'sidebar' }), description: t('toolMode.kanbanDesc'), icon: FiColumns },
    { id: "swot", label: t('swotAnalysis', { ns: 'sidebar' }), description: t('toolMode.swotDesc'), icon: FiTarget },
    { id: "risks", label: t('riskMatrix', { ns: 'sidebar' }), description: t('toolMode.risksDesc'), icon: FiAlertTriangle },
    { id: "triangle", label: t('goldenTriangle', { ns: 'sidebar' }), description: t('toolMode.triangleDesc'), icon: FiTriangle },
  ] as const;

  const renderHomeScreen = () => (
    <>
      <ExportProgressOverlay />
      <div className="min-h-screen flex items-center justify-center bg-background px-6 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
        <div className="max-w-4xl w-full space-y-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/20 text-primary flex items-center justify-center font-semibold">
                {profileInitial}
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('home.connectedAs')}</p>
                <p className="text-foreground font-semibold">{authUser?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={toggleTheme}
                className="min-w-[44px] min-h-[44px] p-2 rounded-lg border border-border text-foreground hover:border-primary hover:text-foreground transition flex items-center justify-center"
                aria-label={theme === "dark" ? t('switchToLight', { ns: 'header' }) : t('switchToDark', { ns: 'header' })}
              >
                {theme === "dark" ? <FiSun className="w-5 h-5" /> : <FiMoon className="w-5 h-5" />}
              </button>
              <button
                type="button"
                onClick={logout}
                className="min-w-[44px] min-h-[44px] px-4 py-2 rounded-lg border border-border text-foreground hover:border-red-400 hover:text-foreground transition"
              >
                {t('home.logOut')}
              </button>
            </div>
          </div>
          <div className="text-center space-y-4">
            <div className="flex flex-col items-center gap-4">
              <img
                src={archiPlanLogoHorizontal}
                alt="ArchiPlan"
                className="h-20 sm:h-24 w-auto"
                style={{ transform: "scale(2.7)" }}
                loading="eager"
              />
              <h1 className="text-4xl font-extrabold text-foreground">ArchiPlan</h1>
            </div>
            <p className="text-lg text-muted-foreground">
              {t('home.tagline')}
            </p>
          </div>
          {isGuest && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl px-5 py-4 text-sm text-amber-600">
              {t('home.guestWarning')}
            </div>
          )}
          {isGuest && guestHomeMode === "project" && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setGuestHomeMode("choice")}
                className="px-4 py-2 rounded-lg border border-border text-foreground hover:border-primary hover:text-foreground transition"
              >
                {t('toolMode.changeMode')}
              </button>
            </div>
          )}
          {isGuest && guestHomeMode === "choice" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
                <h3 className="text-xl font-semibold text-foreground">{t('toolMode.title')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('toolMode.useSingleModule')}
                </p>
                <button
                  type="button"
                  onClick={() => setGuestHomeMode("tools")}
                  className="w-full px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition"
                >
                  {t('toolMode.chooseATool')}
                </button>
              </div>
              <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
                <h3 className="text-xl font-semibold text-foreground">{t('toolMode.projectMode')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('toolMode.testFullApp')}
                </p>
                <button
                  type="button"
                  onClick={() => setGuestHomeMode("project")}
                  className="w-full px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition"
                >
                  {t('toolMode.switchToProjectMode')}
                </button>
              </div>
            </div>
          )}
          {isGuest && guestHomeMode === "tools" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-foreground">{t('toolMode.chooseATool')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('toolMode.selectModuleQuick')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setGuestHomeMode("choice")}
                  className="px-4 py-2 rounded-lg border border-border text-foreground hover:border-primary hover:text-foreground transition"
                >
                  {t('toolMode.back')}
                </button>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl px-5 py-4 text-sm text-amber-600">
                {t('toolMode.tempSessionWarning')}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {guestToolOptions.map((tool) => {
                  const Icon = tool.icon;
                  return (
                    <button
                      key={tool.id}
                      type="button"
                      onClick={() => enterGuestToolMode(tool.id, tool.label)}
                      className="flex items-start gap-4 bg-card/70 border border-border rounded-2xl p-5 text-left hover:border-primary hover:bg-card transition"
                    >
                      <div className="w-10 h-10 rounded-xl bg-primary/20 text-primary flex items-center justify-center">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="text-foreground font-semibold">{tool.label}</h4>
                        <p className="text-sm text-muted-foreground">{tool.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {(!isGuest || guestHomeMode === "project") && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-card border border-border rounded-2xl p-6 flex flex-col space-y-4">
              <div className="w-12 h-12 rounded-xl bg-primary/15 flex items-center justify-center text-primary">
                <FiDatabase className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-foreground">
                  {t('home.sampleData')}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t('home.sampleDataDesc')}
                </p>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground">
                  {t('home.scenario')}
                </label>
                <select
                  value={sampleScenario}
                  onChange={(event) =>
                    setSampleScenario(event.target.value as "success" | "partial" | "failed" | "day5")
                  }
                  className="w-full px-3 py-2 rounded-lg bg-card border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="success">{t('home.scenarioSuccess')}</option>
                  <option value="partial">{t('home.scenarioPartial')}</option>
                  <option value="failed">{t('home.scenarioFailed')}</option>
                  <option value="day5">{t('home.scenarioDay5')}</option>
                </select>
                <p className="text-xs text-muted-foreground">
                  {t('home.generatesSample')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  // Import dynamique : les données de démo (~1 100 lignes) ne
                  // chargent que si l'utilisateur clique sur "Générer un exemple".
                  import("./data/loadSampleData").then(({ loadSampleData }) => {
                    loadSampleData(sampleScenario);
                    setTimeout(() => setActiveTab('dashboard'), 150);
                  });
                }}
                className="mt-auto px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition"
              >
                {t('home.generateExample')}
              </button>
            </div>
            <div className="bg-card border border-border rounded-2xl p-6 flex flex-col space-y-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/15 flex items-center justify-center text-emerald-400">
                <FiPlus className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-foreground">
                  {t('home.newProject')}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t('home.newProjectDesc')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setNewProjectDialogOpen(true)}
                className="mt-auto px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition"
              >
                {t('home.createProject')}
              </button>
            </div>
            <div className="bg-card border border-border rounded-2xl p-6 flex flex-col space-y-4">
              <div className="w-12 h-12 rounded-xl bg-sky-500/15 flex items-center justify-center text-sky-400">
                <FiFolder className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-foreground">
                  {t('home.loadProject')}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t('home.loadProjectDesc')}
                </p>
              </div>
              <div className="mt-auto space-y-3">
                <button
                  type="button"
                  onClick={() => setLoadDialogOpen(true)}
                  className="w-full px-4 py-2 rounded-lg bg-muted text-foreground hover:bg-muted transition"
                >
                  {t('home.loadFromBrowser')}
                </button>
                <button
                  type="button"
                  onClick={() => setWorkspaceDialogOpen(true)}
                  className="w-full px-4 py-2 rounded-lg border border-border text-foreground hover:border-primary hover:text-foreground transition"
                >
                  {t('home.importWorkspace')}
                </button>
              </div>
            </div>
            {!isGuest && (
              <div className="bg-card border border-border rounded-2xl p-6 flex flex-col space-y-4">
                <div className="w-12 h-12 rounded-xl bg-violet-500/15 flex items-center justify-center text-violet-400">
                  <FiLink className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground">
                    {t('home.joinProject')}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t('home.joinProjectDesc')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setJoinProjectDialogOpen(true)}
                  className="mt-auto px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition"
                >
                  {t('home.enterCode')}
                </button>
              </div>
            )}
          </div>
          )}
          {!isGuest && isOrgManager && (
            <button
              type="button"
              onClick={() => setActiveTab('org-management')}
              className="w-full text-left bg-card border border-border rounded-2xl p-5 flex items-center gap-5 hover:border-primary transition group"
            >
              <div className="w-12 h-12 rounded-xl bg-orange-500/15 flex items-center justify-center text-orange-400 shrink-0">
                <FiBriefcase className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="text-lg font-semibold text-foreground">
                    {t('home.myOrganization', 'Mon Organisation')}
                  </h4>
                  {(authUser.orgMemberRole === 'OWNER' || authUser.orgMemberRole === 'MANAGER') && (
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-orange-500/15 text-orange-400">
                      {authUser.orgMemberRole === 'OWNER' ? t('org.ownerBadge', 'Propriétaire') : t('org.managerBadge', 'Manager')}
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {t('home.myOrganizationDesc', 'Gérez les membres, les rôles et les paramètres de votre organisation.')}
                </p>
              </div>
              <FiChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition shrink-0" />
            </button>
          )}
          {!isGuest && (
            <div className="bg-card/50 border border-border rounded-2xl p-6 space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h4 className="text-lg font-semibold text-foreground">{t('home.pendingInvites')}</h4>
                  <p className="text-sm text-muted-foreground">
                    {t('home.pendingInvitesDesc')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => void loadPendingInvites()}
                  className="px-4 py-2 rounded-lg border border-border text-foreground hover:border-primary hover:text-foreground transition"
                >
                  {t('home.refresh')}
                </button>
              </div>
              {invitesError && (
                <div className="text-sm text-red-600 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
                  {invitesError}
                </div>
              )}
              {invitesLoading ? (
                <p className="text-sm text-muted-foreground">{t('home.loadingInvites')}</p>
              ) : pendingInvites.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t('home.noInvites')}
                </p>
              ) : (
                <div className="space-y-3">
                  {pendingInvites.map((invite) => (
                    <div
                      key={invite.id}
                      className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 bg-card/70 border border-border rounded-xl p-4"
                    >
                      <div>
                        <p className="text-foreground font-semibold">
                          {invite.project?.name ?? t('home.sharedProject')}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t('home.invitedBy', { name: invite.invitedBy.name ?? invite.invitedBy.email })}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t('home.proposedRole', { role: invite.role })}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleAcceptInviteFromHome(invite.token)}
                        className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition"
                      >
                        {t('home.join')}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="bg-card/50 border border-border rounded-2xl p-6">
            <h4 className="text-lg font-semibold text-foreground mb-3">
              {t('home.quickShortcuts')}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-foreground">
                  1
                </span>
                <p>
                  {t('home.shortcut1')}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-foreground">
                  2
                </span>
                <p>
                  {t('home.shortcut2')}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-foreground">
                  3
                </span>
                <p>
                  {t('home.shortcut3')}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-foreground">
                  4
                </span>
                <p>{t('home.shortcut4')}</p>
              </div>
            </div>
          </div>
        </div>
        <Suspense fallback={null}>
          <NewProjectDialog
            isOpen={newProjectDialogOpen}
            onClose={() => setNewProjectDialogOpen(false)}
            onCreated={() => setActiveTab('dashboard')}
          />
        </Suspense>
        <LoadDialog
          isOpen={loadDialogOpen}
          onClose={() => setLoadDialogOpen(false)}
          onLoaded={() => setActiveTab('dashboard')}
        />
        <Suspense fallback={null}>
          <WorkspaceDialog
            isOpen={workspaceDialogOpen}
            onClose={() => setWorkspaceDialogOpen(false)}
          />
        </Suspense>
        <Suspense fallback={null}>
          <JoinProjectDialog
            isOpen={joinProjectDialogOpen}
            onClose={() => setJoinProjectDialogOpen(false)}
            onJoined={() => {
              setActiveTab("dashboard");
              void loadPendingInvites();
            }}
          />
        </Suspense>
      </div>
    </>
  );

  if (isGlobalAdmin && activeTab !== "home") {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <ExportProgressOverlay />
        <header className="border-b border-border bg-card/80 backdrop-blur px-4 sm:px-8 py-4 pt-[calc(1rem+env(safe-area-inset-top))] flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-sm text-muted-foreground uppercase tracking-wide">{t('admin.consoleTitle')}</p>
            <h1 className="text-2xl font-bold">{adminConsoleDisplayName}</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setActiveTab("home")}
              className="flex items-center gap-2 px-3 py-2 rounded-xl border border-border text-sm text-foreground hover:border-primary transition"
              aria-label={t('tabs.overview', { ns: 'admin', defaultValue: 'Accueil' })}
            >
              <FiHome className="w-4 h-4" />
              <span className="hidden sm:inline">{t('tabs.overview', { ns: 'admin', defaultValue: 'Accueil' })}</span>
            </button>
            <button
              type="button"
              onClick={toggleTheme}
              className="p-2 rounded-xl border border-border text-foreground hover:border-primary transition"
              aria-label={theme === "dark" ? t('switchToLight', { ns: 'header' }) : t('switchToDark', { ns: 'header' })}
            >
              {theme === "dark" ? <FiSun className="w-5 h-5" /> : <FiMoon className="w-5 h-5" />}
            </button>
            <LanguageSwitcher />
            <span className="text-sm text-muted-foreground hidden sm:block">
              {t('admin.role')} : {t(`roles.${adminConsoleRole}`)}
            </span>
            <button
              type="button"
              onClick={logout}
              className="px-4 py-2 rounded-xl border border-border text-sm hover:border-red-400 transition"
            >
              {t('home.logOut')}
            </button>
          </div>
        </header>
        <main className="px-4 sm:px-8 py-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
          <Suspense fallback={<PageSkeleton />}>
            <AdminConsole adminName={adminConsoleDisplayName} adminRole={adminConsoleRole} />
          </Suspense>
        </main>
      </div>
    );
  }

  // Afficher la page d'accueil si pas de projet actif OU si activeTab = "home"
  // Exception : le portail organisation est accessible sans projet actif
  if ((!hasActiveProject || activeTab === "home") && activeTab !== "org-management") {
    return renderHomeScreen();
  }

  return (
    <>
      <ExportProgressOverlay />
      <RootLayout activeTab={activeTab} onNavigate={setActiveTab}>
        <Suspense fallback={<PageSkeleton />}>
        <AnimatedPage key={activeTab}>
          <div className="space-y-6">
              {/* PERT View */}
              {activeTab === "pert" && <PertPageWrapper calculatePert={calculatePert} />}

              {/* Dashboard View */}
              {activeTab === "dashboard" && (
                <Dashboard
                  onNavigate={setActiveTab}
                  onSelectClient={(clientId) => {
                    setClientFilterId(clientId);
                    setActiveTab("clients");
                  }}
                />
              )}

              {/* GANTT View */}
              {activeTab === "gantt" && <GanttPageWrapper />}

              {/* RACI View */}
              {activeTab === "raci" && <RaciPageWrapper />}

              {/* WBS View */}
              {activeTab === "wbs" && <WbsPageWrapper />}

              {/* Triangle d'Or View */}
              {activeTab === "triangle" && <TrianglePageWrapper />}

              {/* Risk Matrix View */}
              {activeTab === "risks" && <RisksPageWrapper />}

              {/* SWOT Analysis View */}
              {activeTab === "swot" && <SwotPageWrapper />}

              {/* Budget & EVM View */}
              {activeTab === "budget" && <BudgetPageWrapper />}

              {/* Kanban Board View */}
              {activeTab === "kanban" && <KanbanPageWrapper />}

              {/* Project Closure View */}
              {activeTab === "closure" && <ClosurePageWrapper />}

              {/* Clients Management View */}
              {activeTab === "clients" && (
                <ClientsPageWrapper
                  onNavigateToSpecs={(clientId) => {
                    setClientFilterId(clientId);
                    setActiveTab("specifications");
                  }}
                  initialClientFilter={clientFilterId}
                  onClientFilterChange={setClientFilterId}
                />
              )}

              {/* Specifications Management View */}
              {activeTab === "specifications" && (
                <SpecificationsPageWrapper
                  editingSpecificationId={editingSpecificationId}
                  onEditSpecification={setEditingSpecificationId}
                  initialClientFilter={clientFilterId}
                  onClientFilterChange={setClientFilterId}
                />
              )}

              {/* Settings View */}
              {activeTab === "settings" && (
                <SettingsPageWrapper
                  onSave={settingsSubTab === "project" && currentProject ? handleSaveSettings : undefined}
                  hasChanges={settingsSubTab === "project" && hasSettingsChanges}
                  isSaving={isSavingSettings}
                >
                  {/* Sub-tab switcher */}
                  <div className="flex gap-1 bg-muted/50 border border-border rounded-xl p-1 mb-6">
                    <button
                      type="button"
                      onClick={() => setSettingsSubTab("account")}
                      className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition ${settingsSubTab === "account" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                    >
                      <FiUser size={14} />
                      Mon compte
                    </button>
                    <button
                      type="button"
                      onClick={() => setSettingsSubTab("project")}
                      className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition ${settingsSubTab === "project" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                    >
                      <FiSettings size={14} />
                      Projet
                    </button>
                    {!isGuest && isOrgManager && (
                      <button
                        type="button"
                        onClick={() => setActiveTab('org-management')}
                        className="flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition text-muted-foreground hover:text-foreground"
                      >
                        <FiBriefcase size={14} />
                        Organisation
                      </button>
                    )}
                  </div>

                  {/* Account settings */}
                  {settingsSubTab === "account" && (
                    <>
                      <UserAccountSettings />
                      {/* Affichage de l'affiliation org pour les membres non-gestionnaires */}
                      {!isGuest && authUser?.organizationId && !isOrgManager && (
                        <div className="mt-8 bg-card/60 border border-border rounded-xl p-5 space-y-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-orange-500/15 flex items-center justify-center text-orange-400 shrink-0">
                              <FiBriefcase className="w-4 h-4" />
                            </div>
                            <h4 className="text-base font-semibold text-foreground">Mon entreprise</h4>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                            <div className="space-y-1">
                              <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Rôle</p>
                              <p className="text-foreground font-medium capitalize">
                                {authUser.orgMemberRole === 'MEMBER'  ? 'Membre'
                                 : authUser.orgMemberRole === 'VIEWER' ? 'Observateur'
                                 : authUser.orgMemberRole ?? '—'}
                              </p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Statut</p>
                              <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${
                                authUser.orgMemberStatus === 'ACTIVE'   ? 'bg-emerald-500/15 text-emerald-400'
                                : authUser.orgMemberStatus === 'PENDING' ? 'bg-amber-500/15 text-amber-400'
                                : 'bg-muted text-muted-foreground'
                              }`}>
                                {authUser.orgMemberStatus === 'ACTIVE'   ? 'Actif'
                                 : authUser.orgMemberStatus === 'PENDING' ? 'En attente'
                                 : authUser.orgMemberStatus ?? '—'}
                              </span>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Pour gérer les membres ou les paramètres de l'organisation, contactez votre administrateur.
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  {/* Project settings */}
                  {settingsSubTab === "project" && currentProject ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
                      <div className="space-y-5">
                        <label className="block text-sm text-foreground font-medium">
                          {t('settings.projectStartDate')}
                          <input
                            type="date"
                            value={startDateDraft}
                            onChange={(e) =>
                              handleStartDateChange(e.target.value)
                            }
                            className="mt-2 w-full bg-card border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:border-primary"
                          />
                        </label>

                        <label className="block text-sm text-foreground font-medium">
                          {t('settings.projectCurrency')}
                          <select
                            value={currencyDraft}
                            onChange={(e) => setCurrencyDraft(e.target.value)}
                            className="mt-2 w-full bg-card border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:border-primary"
                          >
                            <option value="EUR">EUR (€) - Euro</option>
                            <option value="USD">USD ($) - Dollar américain</option>
                            <option value="GBP">GBP (£) - Livre sterling</option>
                            <option value="CHF">CHF - Franc suisse</option>
                            <option value="CAD">CAD ($) - Dollar canadien</option>
                            <option value="JPY">JPY (¥) - Yen japonais</option>
                            <option value="CNY">CNY (¥) - Yuan chinois</option>
                            <option value="AUD">AUD ($) - Dollar australien</option>
                            <option value="INR">INR (₹) - Roupie indienne</option>
                            <option value="BRL">BRL (R$) - Real brésilien</option>
                            <option value="RUB">RUB (₽) - Rouble russe</option>
                            <option value="ZAR">ZAR (R) - Rand sud-africain</option>
                            <option value="MXN">MXN ($) - Peso mexicain</option>
                            <option value="SEK">SEK (kr) - Couronne suédoise</option>
                            <option value="NOK">NOK (kr) - Couronne norvégienne</option>
                            <option value="DKK">DKK (kr) - Couronne danoise</option>
                            <option value="PLN">PLN (zł) - Zloty polonais</option>
                            <option value="TRY">TRY (₺) - Livre turque</option>
                            <option value="AED">AED (د.إ) - Dirham des EAU</option>
                            <option value="SAR">SAR (﷼) - Riyal saoudien</option>
                          </select>
                        </label>

                        <label className="flex items-start gap-3 bg-card/70 border border-border rounded-lg p-4">
                          <input
                            type="checkbox"
                            className="mt-1 w-5 h-5 text-primary border-border rounded"
                            checked={includeWeekendsDraft}
                            onChange={(e) =>
                              handleIncludeWeekendsChange(e.target.checked)
                            }
                          />
                          <div>
                            <p className="text-foreground font-semibold">
                              {t('settings.includeWeekends')}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {t('settings.includeWeekendsDesc')}
                            </p>
                          </div>
                        </label>

                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm text-foreground font-medium">
                              {t('settings.holidays')}
                            </label>
                            <button
                              type="button"
                              onClick={handleAutoFillHolidays}
                              className="text-xs px-3 py-1 rounded-full bg-muted border border-border text-primary hover:border-primary transition"
                            >
                              {t('settings.autoFillFrance')}
                            </button>
                          </div>
                          <textarea
                            value={holidayInput}
                            onChange={(e) =>
                              handleHolidayInputChange(e.target.value)
                            }
                            rows={6}
                            className="w-full bg-card border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:border-primary"
                            placeholder={"2025-01-01\n2025-05-01"}
                          />
                          <p className="text-xs text-muted-foreground mt-2">
                            {t('settings.holidayFormat')}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="bg-card border border-border rounded-xl p-5">
                          <h3 className="text-foreground font-semibold mb-2">
                            {t('settings.calendarSummary')}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            Week-ends{" "}
                            {includeWeekendsSetting
                              ? t('settings.weekendsIncluded')
                              : t('settings.weekendsExcluded')}
                            .
                          </p>
                          <div className="mt-4">
                            <p className="text-sm text-foreground font-semibold mb-2">
                              {t('settings.upcomingHolidays')}
                            </p>
                            {upcomingHolidays.length === 0 ? (
                              <p className="text-sm text-muted-foreground">
                                {t('settings.noHolidays')}
                              </p>
                            ) : (
                              <ul className="space-y-2">
                                {upcomingHolidays
                                  .slice(0, 5)
                                  .map((date, index) => (
                                    <li
                                      key={`${date.toISOString()}-${index}`}
                                      className="text-sm text-foreground flex items-center gap-2"
                                    >
                                      <span className="w-2 h-2 rounded-full bg-red-400" />
                                      {date.toLocaleDateString(undefined, {
                                        weekday: "short",
                                        day: "2-digit",
                                        month: "long",
                                        year: "numeric",
                                      })}
                                    </li>
                                  ))}
                                {upcomingHolidays.length > 5 && (
                                  <li className="text-xs text-muted-foreground">
                                    {t('settings.moreHolidays', { count: upcomingHolidays.length - 5 })}
                                  </li>
                                )}
                              </ul>
                            )}
                          </div>
                        </div>
                        <div className="bg-card border border-border rounded-xl p-5">
                          <h3 className="text-foreground font-semibold mb-2">
                            {t('settings.calculationImpact')}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {t('settings.calculationImpactDesc')}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : settingsSubTab === "project" ? (
                    <p className="text-muted-foreground">
                      {t('settings.noActiveProject')}
                    </p>
                  ) : null}
                </SettingsPageWrapper>
              )}

              {/* Organization Management Portal */}
              {activeTab === "org-management" && <OrganizationPanel />}

              {/* Support Center View */}
              {activeTab === "support" && <SupportPageWrapper />}

          </div>
        </AnimatedPage>
        </Suspense>
      </RootLayout>

      {/* Dialogs */}
      <SaveDialog
        isOpen={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
      />
      <LoadDialog
        isOpen={loadDialogOpen}
        onClose={() => setLoadDialogOpen(false)}
        onLoaded={() => setActiveTab('dashboard')}
      />
      <Suspense fallback={null}>
        <ExportDialog
          isOpen={exportDialogOpen}
          onClose={() => setExportDialogOpen(false)}
        />
      </Suspense>
      <Suspense fallback={null}>
        <WorkspaceDialog
          isOpen={workspaceDialogOpen}
          onClose={() => setWorkspaceDialogOpen(false)}
        />
      </Suspense>
      <Suspense fallback={null}>
        <NewProjectDialog
          isOpen={newProjectDialogOpen}
          onClose={() => setNewProjectDialogOpen(false)}
          onCreated={() => setActiveTab('dashboard')}
        />
      </Suspense>
      <Suspense fallback={null}>
        <JoinProjectDialog
          isOpen={joinProjectDialogOpen}
          onClose={() => setJoinProjectDialogOpen(false)}
          onJoined={() => {
            setActiveTab("dashboard");
            void loadPendingInvites();
          }}
        />
      </Suspense>
    </>
  );
}

export default App;
